import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { TwilioConnection } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { CopyableId } from "@/components/ui/CopyableId";
import { Field } from "@/components/ui/Field";
import {
  KeyIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  RefreshIcon,
  TrashIcon,
} from "@/lib/icons";

type State =
  | { status: "loading" }
  | { status: "ready"; connections: TwilioConnection[] }
  | { status: "error"; message: string };

interface PendingStatus {
  conn: TwilioConnection;
  next: "active" | "disconnected";
}

export function ConnectionsPage() {
  const navigate = useNavigate();
  const ctx = useConnectionContext();
  const [state, setState] = useState<State>({ status: "loading" });
  const [pendingStatus, setPendingStatus] = useState<PendingStatus | null>(null);
  const [renaming, setRenaming] = useState<{ conn: TwilioConnection; value: string } | null>(null);
  const [deleting, setDeleting] = useState<TwilioConnection | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncedId, setSyncedId] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.twilio.list();
      setState({ status: "ready", connections: r.connections });
    } catch (e) {
      setState({
        status: "error",
        message: e instanceof Error ? e.message : "Failed to load",
      });
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleStatusChange() {
    if (!pendingStatus) return;
    setBusy(true);
    try {
      await api.twilio.update(pendingStatus.conn.id, { status: pendingStatus.next });
      setPendingStatus(null);
      await load();
      await ctx.refresh();
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : "Update failed";
      setState({ status: "error", message: msg });
      setPendingStatus(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleRename() {
    if (!renaming) return;
    setBusy(true);
    try {
      await api.twilio.update(renaming.conn.id, {
        friendlyName: renaming.value.trim() || null,
      });
      setRenaming(null);
      await load();
      await ctx.refresh();
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : "Rename failed";
      setState({ status: "error", message: msg });
      setRenaming(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleSync(conn: TwilioConnection) {
    setSyncingId(conn.id);
    try {
      await api.twilio.sync(conn.id);
      setSyncedId(conn.id);
      // Sync lands asynchronously — clear the confirmation and refresh
      // lastSyncedAt a few seconds later once the workers have run.
      setTimeout(() => setSyncedId((s) => (s === conn.id ? null : s)), 3000);
      setTimeout(() => void load(), 4000);
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof ApiClientError ? err.message : "Sync failed",
      });
    } finally {
      setSyncingId(null);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.twilio.delete(deleting.id);
      setDeleting(null);
      await load();
      await ctx.refresh();
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : "Delete failed";
      setState({ status: "error", message: msg });
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  }

  const strongTextCls = "text-zinc-900 dark:text-zinc-100";

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Workspace
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Connections
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Manage the Twilio accounts feeding telemetry into TwilioPulse.
          </p>
        </div>
        <Button onClick={() => navigate("/connections/new")}>
          <PlusIcon size={14} />
          New connection
        </Button>
      </header>

      {state.status === "loading" && <SkeletonRows />}
      {state.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      )}
      {state.status === "ready" && state.connections.length === 0 && <EmptyState />}
      {state.status === "ready" && state.connections.length > 0 && (
        <ConnectionsTable
          connections={state.connections}
          syncingId={syncingId}
          syncedId={syncedId}
          onSync={handleSync}
          onRename={(c) => setRenaming({ conn: c, value: c.friendlyName ?? "" })}
          onDeactivate={(c) => setPendingStatus({ conn: c, next: "disconnected" })}
          onReactivate={(c) => setPendingStatus({ conn: c, next: "active" })}
          onDelete={(c) => setDeleting(c)}
        />
      )}

      {/* Status change (deactivate / reactivate) */}
      <ConfirmDialog
        open={pendingStatus !== null}
        title={
          pendingStatus?.next === "disconnected"
            ? "Deactivate connection?"
            : "Reactivate connection?"
        }
        description={
          pendingStatus?.next === "disconnected" ? (
            <>
              We'll stop ingesting telemetry from{" "}
              <strong className={strongTextCls}>
                {pendingStatus?.conn.friendlyName ?? pendingStatus?.conn.accountSid}
              </strong>{" "}
              until you reactivate it. Your existing data and credentials are kept.
            </>
          ) : (
            <>
              We'll resume ingestion from{" "}
              <strong className={strongTextCls}>
                {pendingStatus?.conn.friendlyName ?? pendingStatus?.conn.accountSid}
              </strong>{" "}
              using the stored credentials.
            </>
          )
        }
        confirmLabel={pendingStatus?.next === "disconnected" ? "Deactivate" : "Reactivate"}
        intent={pendingStatus?.next === "disconnected" ? "danger" : "primary"}
        busy={busy}
        onConfirm={handleStatusChange}
        onCancel={() => setPendingStatus(null)}
      />

      {/* Rename dialog */}
      <RenameDialog
        open={renaming !== null}
        value={renaming?.value ?? ""}
        onChange={(v) => setRenaming((r) => (r ? { ...r, value: v } : r))}
        busy={busy}
        onConfirm={handleRename}
        onCancel={() => setRenaming(null)}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete connection?"
        description={
          <>
            This will permanently remove{" "}
            <strong className={strongTextCls}>
              {deleting?.friendlyName ?? deleting?.accountSid}
            </strong>{" "}
            and all its ingested telemetry. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        intent="danger"
        busy={busy}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function RenameDialog({
  open,
  value,
  onChange,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  });

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    inputRef.current?.focus();
    inputRef.current?.select();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancelRef.current();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm dark:bg-zinc-950/70"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white p-5 shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:ring-1 dark:ring-inset dark:ring-white/[0.03]">
        <h2
          id="rename-title"
          className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50"
        >
          Rename connection
        </h2>
        <p className="mt-1 text-[13px] text-zinc-600 dark:text-zinc-400">
          Leave blank to fall back to the Account SID.
        </p>
        <div className="mt-4">
          <Field
            ref={inputRef}
            label="Friendly name"
            placeholder="e.g. Production account"
            value={value}
            maxLength={120}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onConfirm();
            }}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConnectionsTable({
  connections,
  syncingId,
  syncedId,
  onSync,
  onRename,
  onDeactivate,
  onReactivate,
  onDelete,
}: {
  connections: TwilioConnection[];
  syncingId: string | null;
  syncedId: string | null;
  onSync: (c: TwilioConnection) => void;
  onRename: (c: TwilioConnection) => void;
  onDeactivate: (c: TwilioConnection) => void;
  onReactivate: (c: TwilioConnection) => void;
  onDelete: (c: TwilioConnection) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
        <h2 className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-200">All accounts</h2>
        <span className="text-[11.5px] text-zinc-500 dark:text-zinc-500">
          {connections.length} total
        </span>
      </header>
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
        {connections.map((c) => (
          <Row
            key={c.id}
            conn={c}
            syncing={syncingId === c.id}
            synced={syncedId === c.id}
            onSync={() => onSync(c)}
            onRename={() => onRename(c)}
            onDeactivate={() => onDeactivate(c)}
            onReactivate={() => onReactivate(c)}
            onDelete={() => onDelete(c)}
          />
        ))}
      </ul>
    </div>
  );
}

function Row({
  conn,
  syncing,
  synced,
  onSync,
  onRename,
  onDeactivate,
  onReactivate,
  onDelete,
}: {
  conn: TwilioConnection;
  syncing: boolean;
  synced: boolean;
  onSync: () => void;
  onRename: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="grid grid-cols-12 items-center gap-3 px-4 py-3 text-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
      <div className="col-span-12 min-w-0 sm:col-span-4">
        <div className="truncate text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
          {conn.friendlyName ?? conn.accountSid}
        </div>
        <div className="truncate font-mono text-[11.5px] text-zinc-500 dark:text-zinc-500">
          {conn.accountSid}
        </div>
        <div className="mt-0.5 flex items-center gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">
            Connection ID
          </span>
          <CopyableId value={conn.id} label="connection ID" />
        </div>
      </div>
      <div className="col-span-3 sm:col-span-2">
        <StatusPill status={conn.status} />
      </div>
      <div className="col-span-3 text-[12px] text-zinc-500 dark:text-zinc-500 sm:col-span-2">
        {conn.lastSyncedAt ? `Synced ${relativeTime(conn.lastSyncedAt)}` : "Never synced"}
      </div>
      <div className="col-span-6 flex justify-end gap-1 sm:col-span-4">
        {conn.status === "active" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onSync}
            disabled={syncing}
            title="Re-sync this account now"
          >
            <RefreshIcon size={14} />
            <span className="hidden md:inline">
              {syncing ? "Syncing…" : synced ? "Queued ✓" : "Sync now"}
            </span>
          </Button>
        )}
        <Link to={`/connections/${conn.id}/rotate`}>
          <Button size="sm" variant="ghost" title="Rotate API key">
            <KeyIcon size={14} />
            <span className="hidden md:inline">Rotate</span>
          </Button>
        </Link>
        <Button size="sm" variant="ghost" onClick={onRename} title="Rename connection">
          <PencilIcon size={14} />
          <span className="hidden md:inline">Rename</span>
        </Button>
        {conn.status === "disconnected" ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={onReactivate}
            title="Reactivate connection"
          >
            <PlayIcon size={14} />
            <span className="hidden md:inline">Reactivate</span>
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDeactivate}
            title="Deactivate connection"
            className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
          >
            <PauseIcon size={14} />
            <span className="hidden md:inline">Deactivate</span>
          </Button>
        )}
        <Button size="sm" variant="ghost-danger" onClick={onDelete} title="Delete connection">
          <TrashIcon size={14} />
          <span className="hidden md:inline">Delete</span>
        </Button>
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: TwilioConnection["status"] }) {
  const styles: Record<
    TwilioConnection["status"],
    { ring: string; dot: string; text: string; bg: string; glow: string }
  > = {
    active: {
      ring: "ring-emerald-500/30",
      dot: "bg-emerald-500 dark:bg-emerald-400",
      text: "text-emerald-700 dark:text-emerald-300",
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
      glow: "shadow-[0_0_8px_rgba(52,211,153,0.6)]",
    },
    error: {
      ring: "ring-red-500/30",
      dot: "bg-red-500 dark:bg-red-400",
      text: "text-red-700 dark:text-red-300",
      bg: "bg-red-50 dark:bg-red-500/10",
      glow: "shadow-[0_0_8px_rgba(248,113,113,0.55)]",
    },
    disconnected: {
      ring: "ring-zinc-300 dark:ring-zinc-700",
      dot: "bg-zinc-400 dark:bg-zinc-500",
      text: "text-zinc-600 dark:text-zinc-400",
      bg: "bg-zinc-100 dark:bg-zinc-800/60",
      glow: "",
    },
  };
  const s = styles[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${s.ring} ${s.bg} ${s.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot} ${s.glow}`} />
      {status}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-10 text-center shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-brand-500/10 to-transparent" />
      <h2 className="relative text-base font-semibold text-zinc-900 dark:text-zinc-50">
        No connections yet
      </h2>
      <p className="relative mx-auto mt-1.5 max-w-md text-[13.5px] text-zinc-600 dark:text-zinc-400">
        Connect your first Twilio account to start ingesting telemetry. You can connect multiple
        accounts (e.g. Production + Staging) and manage them from this page.
      </p>
      <div className="relative mt-5">
        <Link to="/connections/new">
          <Button>
            <PlusIcon size={14} />
            New connection
          </Button>
        </Link>
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800/80 dark:bg-zinc-900/40">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800/80"
        >
          <div className="space-y-2">
            <div className="h-3 w-40 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-2 w-56 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
          <div className="h-4 w-16 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
        </div>
      ))}
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
